// The Battle builder's data and arithmetic (the page is called "studio" in code): the API calls (staff session or ops key), the live
// duration, fitting the timings to a target length, category grouping, themed rounds, the run sheet edits, the prize and its
// terms, clinical sign-off, and the message for the influencer. Pure functions are unit-tested in studio.test.ts.
import type { BattleTerms, FinishConfig, Format, Look, OpsBattleRow, PrizeConfig, RoundKey, RoundTag, RoundsSettings, SignOff, StateSnapshot, StudioCatalogue, StudioCategory, StudioConfig, StudioCreateRequest, StudioCreateResponse, StudioPreview, StudioPreviewItem, StudioSlot, StudioSwapRequest, StudioSwapResponse, StudioTiming } from '@shared/types';
import { FINISH_LIMITS, PRIZE_DEFAULTS, PROMOTER, ROUND_INFO, STUDIO_DEFAULTS } from '@shared/types';
import { DEFAULT_FINISH } from '@shared/finish';
import { PRE_ROLL_MS, ROUND_CARD_MS } from '@shared/engine';
import { DEFAULT_LOOK, isLook } from '@shared/look';
import type { PoolStage } from '@shared/pool';
import { staffApi, type StaffAuth } from '@/staff/auth';

export const FORMATS: readonly Format[] = ['mcq', 'tf', 'gmcq', 'spot', 'clues', 'number', 'hotspot', 'order', 'multi'];
/** The classic mix plays the five original formats; the four newer ones play in themed rounds only (the server's rule). */
export const CLASSIC_FORMATS: readonly Format[] = ['mcq', 'tf', 'gmcq', 'spot', 'clues'];
export const THEMED_ONLY: readonly Format[] = FORMATS.filter((f) => !CLASSIC_FORMATS.includes(f));
export const FORMAT_LABEL: Record<Format, string> = {
  mcq: 'MCQ', tf: 'True or false', gmcq: 'Graphic MCQ', spot: 'Tap the culprit', clues: 'Diagnose in five',
  number: 'Type the number', hotspot: 'Tap the graphic', order: 'Order the steps', multi: 'Pick all that apply',
};
export const FORMAT_HINT: Record<Format, string> = {
  mcq: 'Four options, one right',
  tf: 'One statement, two big buttons',
  gmcq: 'Bloods, obs or an ECG above four options',
  spot: 'Tap the result that explains the case',
  clues: 'Clues dealt every few seconds; earlier is worth more',
  number: 'Work out a score or value and type it on a keypad',
  hotspot: 'Tap a lead on the ECG or a reading on the monitor',
  order: 'Tap the steps in the right order',
  multi: 'Tap every right answer, or the rarest right one',
};
/** The balance the picker aims for among the allowed formats, adjusted to what the pool can offer (an estimate until the pick). */
export const AUTO_MIX: Record<Format, number> = { mcq: 45, tf: 15, gmcq: 20, spot: 10, clues: 10, number: 6, hotspot: 6, order: 6, multi: 6 };
// 17 Oct 2026: 'gentle' (Year 1–2 quick battles) takes the name Gentle; the easy curve, which had it, is Easy.
export const DIFFICULTY_LABEL: Record<StudioConfig['difficulty'], string> = { ramp: 'Ramp up', mixed: 'Mixed', gentle: 'Gentle', easy: 'Easy', hard: 'Tough' };
export const DIFFICULTY_HINT: Record<StudioConfig['difficulty'], string> = {
  ramp: 'Warm-up questions first, the hardest at the end',
  mixed: 'Any order',
  gentle: 'Mostly warm-up questions, a step up at the end',
  easy: 'Mostly warm-up questions',
  hard: 'Mostly consultant’s questions',
};
export const LOOK_LABEL: Record<Look, string> = { bold: 'Bold', ward: 'Clinical', playful: 'Playful', theatre: 'Theatre', medsnapp: 'MedSnapp' };

export const MIN_QUESTIONS = 5;
export const MAX_QUESTIONS = 100; // 13 Oct: was 40
export const PODIUM_MS = 20_000;
export const LIGHTNING_SEC = 12; // the server's LIGHTNING_MS (api/src/core/itemize.ts); 8 until 15 Oct 2026
export const LIGHTNING_RUN = 5;
/** The lightning run sits in the middle, with at least one ordinary question either side; shorter battles leave it out (as the server does). */
export const LIGHTNING_MIN_QUESTIONS = LIGHTNING_RUN + 2;
/** Diagnose in five always leaves this long to answer once the last clue is down, so its window can run longer than its set time. */
export const AFTER_LAST_CLUE_MS = 5000;
/** Clues a diagnose-in-five question is assumed to deal before the questions are picked (the format's name is the most it deals). */
export const ASSUMED_CLUES = 5;
/** The pre-roll of a round's first question, while its round card shows (the engine's own value). */
export { ROUND_CARD_MS };
export const ANSWER_SEC = { min: 5, max: 90 } as const;
export const VIEW_SEC = { min: 3, max: 30 } as const;
export const CLUE_SEC = { min: 3, max: 10 } as const;
/** A scheduled autopilot start must be at least this far ahead when the battle is created, and within the limit (the server's rule). */
export const MIN_START_LEAD_MS = 30_000;
export const MAX_START_LEAD_MS = 90 * 24 * 60 * 60 * 1000;
/** What a prize may say and offer (the server's limits). */
export const PRIZE_LIMITS = { prize: 80, eligibility: 120, notes: 300, winners: [0, 10], drawPrizes: [0, 20], claimHours: [1, 168] } as const;
/** The name of whoever signs a battle off, and the note they may add (the server's limits). */
export const MAX_SIGNER = 60;
export const MAX_SIGNOFF_NOTE = 300;
/** A source tag (?ref=) as the join page keeps it: letters, numbers, hyphens and underscores, up to 32. */
export const MAX_REF = 32;

// ---------------------------------------------------------------------------
// API (staff session or ops key)
// ---------------------------------------------------------------------------
export type SignOffAction = 'approve' | 'waive' | 'reset';
export interface SignOffRequest { action: SignOffAction; by?: string; note?: string }
export interface SignOffResponse { code: string; signOff: SignOff; version: number; state?: StateSnapshot; warnings?: string[] }

/**
 * The builder's requests. They carry the signed-in staff session, or else the ops key from the link (a bare string is the
 * ops key, as before sign-in existed). A session the server no longer takes is forgotten (see staffApi).
 */
export function studioClient(auth: StaffAuth | string) {
  const who: StaffAuth = typeof auth === 'string' ? { opsKey: auth } : auth;
  const get = <T>(path: string) => staffApi<T>(path, who);
  const post = <T>(path: string, body: unknown) => staffApi<T>(path, who, { method: 'POST', body: JSON.stringify(body) });
  return {
    catalogue: () => get<StudioCatalogue>('/studio/catalogue'),
    preview: (config: StudioConfig) => post<StudioPreview>('/studio/preview', config),
    swap: (req: StudioSwapRequest) => post<StudioSwapResponse>('/studio/swap', req),
    create: (req: StudioCreateRequest) => post<StudioCreateResponse>('/studio/battles', req),
    /** v2.1: the global switches (and, from v2.2, the look for new battles). */
    settings: async () => normaliseSettings(await get<Partial<RoundsSettings>>('/studio/settings')),
    /** Saves the switches; resolves with what the server kept, as it sent it. */
    saveSettings: (s: RoundsSettings) => staffApi<Partial<RoundsSettings>>('/studio/settings', who, { method: 'PUT', body: JSON.stringify(s) }),
    /** v2.1: approve, skip (waive) or reset one battle's sign-off. */
    signOff: (code: string, req: SignOffRequest) => post<SignOffResponse>(`/studio/battles/${encodeURIComponent(code)}/signoff`, req),
    /** The most recent battles, newest first (the ops list's rows, with their sign-off). */
    battles: async () => { const r = await get<{ battles?: OpsBattleRow[] }>('/ops/battles'); return Array.isArray(r.battles) ? r.battles : []; },
  };
}
export type StudioClient = ReturnType<typeof studioClient>;

// ---------------------------------------------------------------------------
// Settings (v2.1 sign-off switches, v2.2 look)
// ---------------------------------------------------------------------------
/** Until staff change them: battles wait for sign-off, hosts may start without it (logged), the bold look, not frozen. */
export const SETTINGS_DEFAULTS: Required<RoundsSettings> = { requireSignOff: true, allowHostOverride: true, look: DEFAULT_LOOK, lookFrozen: false };
export function normaliseSettings(raw: Partial<RoundsSettings> | undefined): Required<RoundsSettings> {
  const r = raw ?? {};
  return {
    requireSignOff: typeof r.requireSignOff === 'boolean' ? r.requireSignOff : SETTINGS_DEFAULTS.requireSignOff,
    allowHostOverride: typeof r.allowHostOverride === 'boolean' ? r.allowHostOverride : SETTINGS_DEFAULTS.allowHostOverride,
    look: isLook(r.look) ? r.look : SETTINGS_DEFAULTS.look,
    lookFrozen: typeof r.lookFrozen === 'boolean' ? r.lookFrozen : SETTINGS_DEFAULTS.lookFrozen,
  };
}
/** The look a new battle gets: the frozen look when frozen, else the battle's own choice, else the look for new battles. */
export const lookFor = (c: Pick<StudioConfig, 'look'>, s: Pick<RoundsSettings, 'look' | 'lookFrozen'>): Look =>
  s.lookFrozen ? (s.look ?? DEFAULT_LOOK) : (c.look ?? s.look ?? DEFAULT_LOOK);

// ---------------------------------------------------------------------------
// Themed rounds (v2.2)
// ---------------------------------------------------------------------------
/** The rounds in their designed order. */
export const ROUND_ORDER: readonly RoundKey[] = ['ward-round', 'bloods', 'bleep', 'diagnose', 'order-pick', 'crash-call'];
/** The formats each round's rule card describes; a question moves only to a round whose rule fits it. Crash call takes any format. */
export const ROUND_FORMATS: Record<RoundKey, readonly Format[]> = {
  'ward-round': ['mcq', 'tf'],
  bloods: ['gmcq', 'spot', 'number', 'hotspot'],
  bleep: ['tf'],
  diagnose: ['clues'],
  'order-pick': ['order', 'multi'],
  'crash-call': FORMATS,
};
export const fitsRound = (format: Format, key: RoundKey) => ROUND_FORMATS[key].includes(format);
/** The rounds a question of this format may sit in, in their designed order. */
export const roundsFor = (format: Format): RoundKey[] => ROUND_ORDER.filter((k) => fitsRound(format, k));

/** Anything that may carry a round: a slot (staff moved it there) or a picked question (the server tagged its item). */
interface RoundCarrier { round?: RoundKey; item?: { round?: Pick<RoundTag, 'key'> } }
/** The round a question is in: where staff moved it, else where the server put it. Undefined in a classic battle. */
export const roundOf = (s: RoundCarrier): RoundKey | undefined => s.round ?? s.item?.round?.key;
export const hasRounds = (items: readonly RoundCarrier[]) => items.some((x) => roundOf(x) !== undefined);
/** Which questions open a round: the first of each run of one round. A round's first question has the longer round-card pre-roll. */
export function roundOpeners(items: readonly RoundCarrier[]): boolean[] {
  return items.map((x, i) => { const k = roundOf(x); return k !== undefined && (i === 0 || roundOf(items[i - 1]) !== k); });
}

export interface RoundGroup<T> { key?: RoundKey; start: number; items: T[] }
/** The running order as rounds: each run of questions in one round, in order (one group without a key when there are no rounds). */
export function groupRounds<T extends RoundCarrier>(items: readonly T[]): RoundGroup<T>[] {
  const out: RoundGroup<T>[] = [];
  items.forEach((x, i) => {
    const key = roundOf(x); const last = out[out.length - 1];
    if (last && last.key === key) last.items.push(x);
    else out.push({ key, start: i, items: [x] });
  });
  return out;
}
/** A round's name and rule, for headers. */
export const roundInfo = (key: RoundKey) => ROUND_INFO[key];

/**
 * Moves question `index` to round `key`: it joins the end of that round, or starts the round in its designed place if the battle
 * has none. A true or false moved into The bleep becomes a lightning question; one moved out stops being one. Formats the round's
 * rule does not cover are refused (the list comes back unchanged).
 */
export function moveToRound<T extends StudioSlot & RoundCarrier>(items: readonly T[], index: number, key: RoundKey): T[] {
  const it = items[index];
  if (!it || !fitsRound(it.format, key) || roundOf(it) === key) return [...items];
  const { lightning: _l, ...rest } = it;
  const moved = { ...rest, round: key, ...(key === 'bleep' && it.format === 'tf' ? { lightning: true } : {}) } as T;
  const others = items.filter((_, i) => i !== index);
  // After the last question already in the round; failing that, before the first question of a later round.
  let at = -1;
  for (let i = others.length - 1; i >= 0; i--) if (roundOf(others[i]) === key) { at = i + 1; break; }
  if (at < 0) {
    const rank = ROUND_ORDER.indexOf(key);
    at = others.findIndex((x) => { const k = roundOf(x); return k !== undefined && ROUND_ORDER.indexOf(k) > rank; });
    if (at < 0) at = others.length;
  }
  return [...others.slice(0, at), moved, ...others.slice(at)];
}

/** Puts one round's questions back into the running order after a drag inside that round. */
export function replaceRound<T>(items: readonly T[], start: number, next: readonly T[]): T[] {
  return [...items.slice(0, start), ...next, ...items.slice(start + next.length)];
}

/** Themed rounds always include The bleep (a lightning run) and Crash call (the double-points finale); the classic mix follows its switches. */
export const isThemed = (c: Pick<StudioConfig, 'structure'>) => c.structure !== 'classic';
/** The formats a battle of this structure can play. */
export const formatsFor = (c: Partial<Pick<StudioConfig, 'structure'>>): readonly Format[] => (isThemed(c) ? FORMATS : CLASSIC_FORMATS);
export const effectiveConfig = (c: StudioConfig): StudioConfig => (isThemed(c) ? { ...c, lightningRound: true, doubleFinal: true } : c);

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------
/** A slot on the clock. A picked question also brings its item, whose clues set how long diagnose in five must run. */
type Timed = Pick<StudioSlot, 'format' | 'lightning' | 'round'> & { item?: { clues?: readonly string[]; round?: Pick<RoundTag, 'key'> } };
type AnswerConfig = Pick<StudioConfig, 'timing'> & Partial<Pick<StudioConfig, 'clueIntervalSec'>>;
type TimingConfig = AnswerConfig & Pick<StudioConfig, 'revealSec' | 'boardSec'>;

/** The gap between clues, in ms. */
export const clueGapMs = (c: Partial<Pick<StudioConfig, 'clueIntervalSec'>>) => (c.clueIntervalSec && c.clueIntervalSec > 0 ? c.clueIntervalSec : 5) * 1000;
/** The shortest window that deals every one of `clues` clues and still leaves time to answer after the last. */
export const minCluesMs = (clues: number, c: Partial<Pick<StudioConfig, 'clueIntervalSec'>>) => Math.max(0, clues - 1) * clueGapMs(c) + AFTER_LAST_CLUE_MS;

/**
 * The answer window, as the server will set it: LIGHTNING_SEC for a lightning true or false; diagnose in five never shorter than it takes to
 * deal every clue and leave time to answer; otherwise the format's time.
 */
export function answerMs(s: Timed, c: AnswerConfig): number {
  if (s.lightning && s.format === 'tf') return LIGHTNING_SEC * 1000;
  const set = (c.timing[s.format] ?? STUDIO_DEFAULTS.timing[s.format]) * 1000;
  return s.format === 'clues' ? Math.max(set, minCluesMs(s.item?.clues?.length ?? ASSUMED_CLUES, c)) : set;
}
/** One question on the clock: the pre-roll (longer when it opens a round), the answer window, the answer view and the leaderboard. */
export const slotMs = (s: Timed, c: TimingConfig, opener = false) => (opener ? ROUND_CARD_MS : PRE_ROLL_MS) + answerMs(s, c) + (c.revealSec + c.boardSec) * 1000;
/** Every question on the clock, round openers included. */
export function slotTimes(slots: readonly Timed[], c: TimingConfig): number[] {
  const open = roundOpeners(slots);
  return slots.map((s, i) => slotMs(s, c, open[i]));
}
/** The whole battle: every question, then the podium (the same sum the server returns as totalMs). */
export const totalMs = (slots: readonly Timed[], c: TimingConfig) => slotTimes(slots, c).reduce((sum, ms) => sum + ms, 0) + PODIUM_MS;

/** Whether a battle of `count` questions gets its lightning round. */
export const lightningFits = (c: Pick<StudioConfig, 'lightningRound'>, count: number) => c.lightningRound && count >= LIGHTNING_MIN_QUESTIONS;

/**
 * The note for a diagnose-in-five window stretched past its set time so every clue is dealt (the words the server uses at the pick),
 * worked out from the questions and timing on screen now so it follows every change.
 */
export function clueStretchNote(items: Timed[], c: AnswerConfig): string | undefined {
  const longest = Math.max(0, ...items.filter((x) => x.format === 'clues').map((x) => answerMs(x, c)));
  if (longest <= c.timing.clues * 1000) return undefined;
  const s = Math.round(longest / 1000);
  return `Diagnose in five needs ${s} s to deal every clue and leave time to answer, so it runs for ${s} s.`;
}
/** True for the server's version of that note, which goes stale as soon as the timing changes. */
export const isClueStretchNote = (w: string) => /^Diagnose in five needs \d+ s to deal every clue/.test(w);

export interface Availability { total: number; formats: Record<Format, number>; difficulty: Record<'easy' | 'medium' | 'hard', number> }
const zeroFormats = (): Record<Format, number> => ({ mcq: 0, tf: 0, gmcq: 0, spot: 0, clues: 0, number: 0, hotspot: 0, order: 0, multi: 0 });

/** 18 Oct 2026: the builder's level (clinical unless set), and a category's (clinical unless the catalogue says Year 1–2). */
export const stageOfConfig = (c: Pick<StudioConfig, 'stage'>): PoolStage => (c.stage === 'preclinical' ? 'preclinical' : 'clinical');
const stageOfCategory = (c: Pick<StudioCategory, 'stage'>): PoolStage => (c.stage === 'preclinical' ? 'preclinical' : 'clinical');
/** The catalogue's categories at a level. */
export const levelCategories = (cat: Pick<StudioCatalogue, 'categories'>, stage: PoolStage) => cat.categories.filter((c) => stageOfCategory(c) === stage);
/** Picking a level: no categories (every category of it), and the difficulty and formats it starts with: Year 1–2 the gentle curve without ordering or calculations, as Make a battle. */
export function levelSettings(stage: PoolStage, c: Pick<StudioConfig, 'formats'>): Pick<StudioConfig, 'stage' | 'categories' | 'difficulty' | 'formats'> {
  const year12 = stage === 'preclinical';
  return { stage, categories: [], difficulty: year12 ? 'gentle' : 'ramp', formats: { ...c.formats, order: !year12, number: !year12 } };
}

/** What the pool holds in the chosen categories; none chosen means every category of the level. A format the server does not count is none. */
export function availableIn(cat: Pick<StudioCatalogue, 'categories'>, chosen: string[], stage: PoolStage = 'clinical'): Availability {
  const pick = new Set(chosen);
  const out: Availability = { total: 0, formats: zeroFormats(), difficulty: { easy: 0, medium: 0, hard: 0 } };
  for (const c of cat.categories) {
    if (pick.size ? !pick.has(c.category) : stageOfCategory(c) !== stage) continue;
    out.total += c.count;
    for (const f of FORMATS) out.formats[f] += c.formats?.[f] ?? 0;
    for (const d of ['easy', 'medium', 'hard'] as const) out.difficulty[d] += c.difficulty?.[d] ?? 0;
  }
  return out;
}

/** The share of each format the picker aims for: the auto balance over the allowed formats that the pool can serve. */
export function expectedMix(c: Pick<StudioConfig, 'formats'> & Partial<Pick<StudioConfig, 'structure'>>, avail?: Availability): Record<Format, number> {
  const usable = formatsFor(c).filter((f) => c.formats[f] && (!avail || avail.formats[f] > 0));
  const weight = usable.reduce((s, f) => s + AUTO_MIX[f], 0);
  const mix = zeroFormats();
  if (!weight) { mix.mcq = 1; return mix; }
  for (const f of usable) mix[f] = AUTO_MIX[f] / weight;
  return mix;
}

/** Whole questions per format for a count, largest remainder first; a lightning round reserves five true or false. */
export function plannedFormats(c: Pick<StudioConfig, 'formats' | 'lightningRound' | 'questionCount'> & Partial<Pick<StudioConfig, 'structure'>>, avail?: Availability, count = c.questionCount): Record<Format, number> {
  const lightning = lightningFits(c, count) ? LIGHTNING_RUN : 0;
  const rest = count - lightning;
  const mix = expectedMix(c, avail);
  const raw = FORMATS.map((f) => ({ f, exact: mix[f] * rest }));
  const plan = zeroFormats();
  let given = 0;
  for (const r of raw) { plan[r.f] = Math.floor(r.exact); given += plan[r.f]; }
  raw.sort((a, b) => (b.exact - Math.floor(b.exact)) - (a.exact - Math.floor(a.exact)) || AUTO_MIX[b.f] - AUTO_MIX[a.f]);
  for (let i = 0; given < rest; i = (i + 1) % raw.length) { if (mix[raw[i].f] > 0) { plan[raw[i].f]++; given++; } }
  plan.tf += lightning;
  return plan;
}

/** The rounds a themed battle of `count` questions is expected to have before the pick: those its formats can fill, at most one per question. */
export function expectedRounds(c: Pick<StudioConfig, 'formats' | 'structure'>, avail?: Availability, count = MAX_QUESTIONS): number {
  if (!isThemed(c)) return 0;
  const usable = (f: Format) => Boolean(c.formats[f]) && (!avail || avail.formats[f] > 0);
  const rounds = ROUND_ORDER.filter((k) => k === 'crash-call' || ROUND_FORMATS[k].some(usable)).length;
  return Math.min(rounds, count);
}

/** The expected length of a battle before the questions are picked: the planned format mix on the configured clock. */
export function estimateTotalMs(input: StudioConfig, avail?: Availability, count = input.questionCount): number {
  const c = effectiveConfig(input);
  const plan = plannedFormats(c, avail, count);
  const lightning = lightningFits(c, count) ? LIGHTNING_RUN : 0;
  let ms = PODIUM_MS + lightning * slotMs({ format: 'tf', lightning: true }, c) + expectedRounds(c, avail, count) * (ROUND_CARD_MS - PRE_ROLL_MS);
  for (const f of FORMATS) ms += (plan[f] - (f === 'tf' ? lightning : 0)) * slotMs({ format: f }, c);
  return ms;
}

export const clampCount = (n: number) => Math.min(MAX_QUESTIONS, Math.max(MIN_QUESTIONS, Math.round(n)));

// ---------------------------------------------------------------------------
// Length: a target the timings are fitted to (the question count stays as set)
// ---------------------------------------------------------------------------
/** The answer seconds the length fit may give each format: around its default, never rushed, never a long wait. */
export const FIT_ANSWER_SEC: Record<Format, readonly [number, number]> = {
  mcq: [10, 60], tf: [5, 30], gmcq: [15, 90], spot: [10, 60], clues: [20, 90], number: [15, 90], hotspot: [10, 60], order: [15, 90], multi: [15, 90],
};
/** The answer view and leaderboard seconds the fit may use, once the answer times alone cannot reach the length. */
export const FIT_VIEW_SEC = { min: 4, max: 30 } as const; // 18 Oct 2026: max 30, above the new 15 s answer view
/** The longest length the builder asks for, in minutes. */
export const MAX_LENGTH_MIN = 120;
/** A fit this close to the target leaves the answer view and the leaderboard alone. */
const FIT_SLACK_MS = 10_000;

export type FitTiming = Pick<StudioConfig, 'timing' | 'revealSec' | 'boardSec'>;
export interface LengthFit extends FitTiming {
  /** The battle's length with these timings. */
  totalMs: number;
  /** The shortest and the longest these questions can run within the bounds. */
  minMs: number; maxMs: number;
  /** True when the length rounds to the minutes asked for. */
  met: boolean;
}

/** Every format's default answer time stretched or shrunk by `k`, in whole seconds, within its bounds. */
export function answerTimesAt(k: number): StudioTiming {
  const t = { ...STUDIO_DEFAULTS.timing };
  for (const f of FORMATS) { const [lo, hi] = FIT_ANSWER_SEC[f]; t[f] = Math.min(hi, Math.max(lo, Math.round(STUDIO_DEFAULTS.timing[f] * k))); }
  return t;
}
const K_MIN = Math.min(...FORMATS.map((f) => FIT_ANSWER_SEC[f][0] / STUDIO_DEFAULTS.timing[f]));
const K_MAX = Math.max(...FORMATS.map((f) => FIT_ANSWER_SEC[f][1] / STUDIO_DEFAULTS.timing[f]));
/** The stretch factors to try, nearest the defaults first, so a tie keeps the timings closest to the defaults. */
const K_STEPS: number[] = Array.from({ length: Math.round((K_MAX - K_MIN) * 100) + 1 }, (_, i) => Math.round((K_MIN + i / 100) * 100) / 100)
  .sort((a, b) => Math.abs(a - 1) - Math.abs(b - 1) || a - b);
const view = (sec: number) => Math.min(FIT_VIEW_SEC.max, Math.max(FIT_VIEW_SEC.min, sec));

/**
 * Fits the timings to a target length without touching the question count. Every answer time is its format's default stretched or
 * shrunk by one factor, within the format's bounds; only when that cannot reach the target do the answer view and the leaderboard
 * stretch or shrink as well. It always starts from the defaults, so the same target and questions give the same timings, however
 * often it runs. `lengthOf` is the battle's length for candidate timings (the picked questions, or the plan before the pick).
 * A target out of reach gives the nearest length these questions can run, with `met` false.
 */
export function fitLength(targetMs: number, lengthOf: (t: FitTiming) => number): LengthFit {
  let best: (FitTiming & { totalMs: number }) | undefined;
  const consider = (t: FitTiming) => {
    const totalMs = lengthOf(t);
    if (!best || Math.abs(totalMs - targetMs) < Math.abs(best.totalMs - targetMs)) best = { ...t, totalMs };
  };
  const views = { revealSec: STUDIO_DEFAULTS.revealSec, boardSec: STUDIO_DEFAULTS.boardSec };
  for (const k of K_STEPS) consider({ timing: answerTimesAt(k), ...views });
  if (Math.abs(best!.totalMs - targetMs) > FIT_SLACK_MS) {
    // Out of the answer times' reach: the views stretch or shrink together, the smallest change first.
    const timing = best!.timing;
    const steps = Array.from({ length: FIT_VIEW_SEC.max - FIT_VIEW_SEC.min + 1 + Math.abs(STUDIO_DEFAULTS.revealSec - STUDIO_DEFAULTS.boardSec) }, (_, i) => FIT_VIEW_SEC.min - Math.max(STUDIO_DEFAULTS.revealSec, STUDIO_DEFAULTS.boardSec) + i)
      .sort((a, b) => Math.abs(a) - Math.abs(b) || a - b);
    for (const d of steps) consider({ timing, revealSec: view(STUDIO_DEFAULTS.revealSec + d), boardSec: view(STUDIO_DEFAULTS.boardSec + d) });
  }
  const fit = best!;
  const minMs = lengthOf({ timing: answerTimesAt(K_MIN), revealSec: FIT_VIEW_SEC.min, boardSec: FIT_VIEW_SEC.min });
  const maxMs = lengthOf({ timing: answerTimesAt(K_MAX), revealSec: FIT_VIEW_SEC.max, boardSec: FIT_VIEW_SEC.max });
  return { timing: fit.timing, revealSec: fit.revealSec, boardSec: fit.boardSec, totalMs: fit.totalMs, minMs, maxMs, met: Math.round(fit.totalMs / 60_000) === Math.round(targetMs / 60_000) };
}
/** True when the config already has these timings. */
export const sameTiming = (a: FitTiming, b: FitTiming) =>
  a.revealSec === b.revealSec && a.boardSec === b.boardSec && FORMATS.every((f) => a.timing[f] === b.timing[f]);

/** The average answer time in whole seconds, lightning questions left out (their 8 s is fixed): from the picked questions, else the plan. */
export function averageAnswerSec(input: StudioConfig, slots?: readonly Timed[], avail?: Availability): number {
  const c = effectiveConfig(input);
  if (slots?.length) {
    const timed = slots.filter((s) => !(s.lightning && s.format === 'tf'));
    return timed.length ? Math.round(timed.reduce((sum, s) => sum + answerMs(s, c), 0) / timed.length / 1000) : LIGHTNING_SEC;
  }
  const plan = plannedFormats(c, avail);
  const lightning = lightningFits(c, c.questionCount) ? LIGHTNING_RUN : 0;
  let n = 0; let ms = 0;
  for (const f of FORMATS) { const k = plan[f] - (f === 'tf' ? lightning : 0); n += k; ms += k * answerMs({ format: f }, c); }
  return n ? Math.round(ms / n / 1000) : LIGHTNING_SEC;
}
/** "5 questions run 3 to 9 minutes": the lengths a fit can reach, for when the target is out of reach. */
export function lengthRangeLine(count: number, minMs: number, maxMs: number): string {
  const lo = Math.max(1, Math.round(minMs / 60_000)); const hi = Math.max(lo, Math.round(maxMs / 60_000));
  return `${count} ${count === 1 ? 'question runs' : 'questions run'} ${lo === hi ? `about ${lo}` : `${lo} to ${hi}`} ${hi === 1 ? 'minute' : 'minutes'}.`;
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------
export const DOMAINS = ['Medicine', 'Surgery', 'Other', 'Year 1–2'] as const;
export type Domain = (typeof DOMAINS)[number];
export interface CategoryGroup { domain: Domain; categories: StudioCategory[]; count: number }

export function groupCategories(cats: StudioCategory[]): CategoryGroup[] {
  const by: Record<Domain, StudioCategory[]> = { Medicine: [], Surgery: [], Other: [], 'Year 1–2': [] };
  for (const c of cats) by[stageOfCategory(c) === 'preclinical' ? 'Year 1–2' : c.domain === 'Medicine' || c.domain === 'Surgery' ? c.domain : 'Other'].push(c);
  return DOMAINS.map((domain) => {
    const categories = [...by[domain]].sort((a, b) => a.category.localeCompare(b.category, 'en-GB'));
    return { domain, categories, count: categories.reduce((s, c) => s + c.count, 0) };
  }).filter((g) => g.categories.length > 0);
}

// ---------------------------------------------------------------------------
// The run sheet
// ---------------------------------------------------------------------------
/**
 * Double points for the finale: in a themed battle every question in Crash call (the last question when the battle has no Crash
 * call); in the classic mix the last question only, wherever the questions have been moved.
 */
export function withDoubleFinal<T extends StudioSlot & RoundCarrier>(items: T[], doubleFinal: boolean, themed = false): T[] {
  const crash = themed && items.some((x) => roundOf(x) === 'crash-call');
  return items.map((it, i) => {
    const double = doubleFinal && (crash ? roundOf(it) === 'crash-call' : i === items.length - 1);
    if (Boolean(it.double) === double) return it;
    const { double: _drop, ...rest } = it;
    return (double ? { ...rest, double: true } : rest) as T;
  });
}

export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) return list;
  const next = [...list]; const [moved] = next.splice(from, 1); next.splice(to, 0, moved);
  return next;
}

/** The slot fields the server needs, with the round each question sits in so the battle is built exactly as reviewed. */
export function toSlot(s: StudioSlot & RoundCarrier): StudioSlot {
  const round = roundOf(s);
  return { poolId: s.poolId, format: s.format, ...(s.lightning ? { lightning: true } : {}), ...(s.double ? { double: true } : {}), ...(round ? { round } : {}) };
}

/** Only the settings that change which questions are picked; timing, words, mode, look, the prize and the finale are applied without a new pick. */
export function pickKey(input: StudioConfig): string {
  const c = effectiveConfig(input);
  return JSON.stringify([stageOfConfig(c), [...c.categories].sort(), c.questionCount, c.difficulty, formatsFor(c).filter((f) => c.formats[f]), c.lightningRound, c.seed ?? null, isThemed(c) ? 'themed' : 'classic']);
}

// ---------------------------------------------------------------------------
// Clinical sign-off at creation (v2.1)
// ---------------------------------------------------------------------------
/** approve = sign off now as a named person; later = the battle waits for a clinician (when sign-off is required); skip = waive it. */
export type SignOffChoice = 'approve' | 'later' | 'skip';
export interface SignOffDraft { choice: SignOffChoice; name: string; note: string }
/** What the battle's sign-off turned out to be once it was created. */
export interface SignOffOutcome { status: SignOff['status']; by?: string; reason?: 'not-required'; error?: string }

export function createProblems(c: StudioConfig, items: StudioPreviewItem[], approved: ReadonlySet<string>, now: number, signOff?: SignOffDraft): string[] {
  const out: string[] = [];
  if (!c.title.trim()) out.push('Give the battle a title.');
  if (items.length < MIN_QUESTIONS) out.push(`A battle needs at least ${MIN_QUESTIONS} questions.`);
  else {
    // How many are approved is shown beside Create, so the reason does not count them again.
    if (items.some((i) => !approved.has(i.poolId))) out.push('Approve every question.');
  }
  const at = c.mode === 'autopilot' ? c.scheduledStartAt : undefined;
  if (at !== undefined && at <= now) out.push('The start time has passed. Pick a later time or start it by hand.');
  else if (at !== undefined && at < now + MIN_START_LEAD_MS) out.push('The start time is too close. Pick a time at least a minute away, or start it by hand.');
  else if (at !== undefined && at > now + MAX_START_LEAD_MS) out.push('Pick a start time within 90 days.');
  out.push(...prizeProblems(c.prize));
  if (signOff?.choice === 'approve' && !signOff.name.trim()) out.push('Add your name to approve these questions, or choose another sign-off option.');
  return out;
}

// ---------------------------------------------------------------------------
// The prize (v2.1)
// ---------------------------------------------------------------------------
export const prizeOf = (c: Pick<StudioConfig, 'prize'>): PrizeConfig => ({ ...PRIZE_DEFAULTS, ...c.prize });

export function prizeProblems(p: PrizeConfig | undefined): string[] {
  if (!p?.enabled) return [];
  const out: string[] = [];
  if (!p.prize.trim()) out.push('Describe the prize.');
  if (!p.eligibility.trim()) out.push('Say who can win the prize.');
  if (p.winners + p.drawPrizes < 1) out.push('A prize needs at least one leaderboard winner or draw prize.');
  return out;
}

/** The prize as the server takes it: only when it is offered, trimmed, notes left out when empty. */
/**
 * 6 Oct: the end-of-battle settings as stored in a draft: the switch (on unless it says off, 9 Oct), and text fields only
 * (anything else dropped). `versioned` is false for a draft saved before drafts carried a version (DRAFT_VERSION): the
 * builder then wrote askDetails: false into every draft whether or not anyone had touched the switch, so off there is
 * not a choice and reads as the default (on).
 */
export function sanitiseFinish(raw: unknown, versioned = true): FinishConfig {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const chosen = r.askDetails === true || (r.askDetails === false && versioned);
  const f: FinishConfig = { askDetails: chosen ? r.askDetails === true : DEFAULT_FINISH.askDetails };
  for (const k of ['heading', 'message', 'button', 'thanks', 'trialLink', 'discountCode'] as const) {
    if (typeof r[k] === 'string') f[k] = (r[k] as string).slice(0, FINISH_LIMITS[k]);
  }
  return f;
}
/** The end-of-battle settings for the create request: always sent, the switch on unless it was turned off (9 Oct), wording trimmed, empty left out. */
export function finishForRequest(f: FinishConfig | undefined): FinishConfig {
  const out: FinishConfig = { askDetails: typeof f?.askDetails === 'boolean' ? f.askDetails : DEFAULT_FINISH.askDetails };
  for (const k of ['heading', 'message', 'button', 'thanks', 'trialLink', 'discountCode'] as const) {
    const v = f?.[k]?.trim();
    if (v) out[k] = v;
  }
  return out;
}
/** What would stop the server taking the end-of-battle settings, in words for the builder. */
export function finishProblems(f: FinishConfig | undefined): string[] {
  const out: string[] = [];
  const link = f?.trialLink?.trim();
  if (link) {
    let ok = false;
    try { ok = new URL(link.replace(/\{(email|first|last|code)\}/g, 'x')).protocol === 'https:'; } catch { ok = false; }
    if (!ok) out.push('The trial link needs to be a full https:// address.');
  }
  const code = f?.discountCode?.trim();
  if (code && !/^[A-Za-z0-9_-]+$/.test(code)) out.push('The discount code can use letters, digits, dashes and underscores.');
  return out;
}

export function prizeForRequest(p: PrizeConfig | undefined): PrizeConfig | undefined {
  if (!p?.enabled) return undefined;
  const notes = p.notes?.trim();
  return {
    enabled: true, prize: p.prize.trim().replace(/\s+/g, ' '), winners: p.winners, drawPrizes: p.drawPrizes,
    eligibility: p.eligibility.trim().replace(/\s+/g, ' '), claimHours: p.claimHours, ...(notes ? { notes } : {}),
  };
}

/** Placeholders for the terms preview: the code and join link exist only once the battle is created. */
export const TERMS_CODE_PLACEHOLDER = '[code]';
/** The battle's public terms data as it will be once created, for previewing the prize terms from the draft. */
export function draftTerms(c: StudioConfig, now: number, code = TERMS_CODE_PLACEHOLDER): BattleTerms {
  const prize = prizeForRequest(c.prize);
  const host = c.hostName?.trim();
  const at = c.mode === 'autopilot' ? c.scheduledStartAt : undefined;
  return {
    code, title: c.title.trim() || 'Untitled battle', ...(host ? { hostName: host } : {}), ...(prize ? { prize } : {}),
    ...(at !== undefined ? { scheduledStartAt: at } : {}), createdAt: now, promoter: { ...PROMOTER },
  };
}

// ---------------------------------------------------------------------------
// The source tag (?ref=) on the join link
// ---------------------------------------------------------------------------
/** A source tag as the join page keeps it: letters, numbers, hyphens and underscores only, up to 32 characters. */
export const cleanRef = (raw: string) => raw.replace(/[^A-Za-z0-9_-]/g, '').slice(0, MAX_REF);
/** The link with ?ref=<tag> (or &ref= when it already has a query), before any #fragment; unchanged without a tag. */
export function withRef(url: string, ref: string): string {
  const tag = cleanRef(ref);
  if (!tag) return url;
  const hash = url.indexOf('#');
  const base = hash >= 0 ? url.slice(0, hash) : url; const frag = hash >= 0 ? url.slice(hash) : '';
  return `${base}${base.includes('?') ? '&' : '?'}ref=${tag}${frag}`;
}

// ---------------------------------------------------------------------------
// Remembered on this device: the draft (so a reload keeps the settings) and the signer's name
// ---------------------------------------------------------------------------
const DRAFT = 'rounds.studio.draft';
/**
 * 9 Oct: the draft's version, stamped by saveDraft. A draft without one was saved before the end of the battle was on by
 * default, so its askDetails: false is read as the default (sanitiseFinish). Raise it when a stored field changes meaning.
 */
export const DRAFT_VERSION = 2;
const SIGNER = 'rounds.studio.signer';
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const wholeIn = (v: unknown, [min, max]: readonly [number, number], fallback: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v))) : fallback);

/** A remembered prize, made safe: every field present and inside the server's limits. */
export function sanitisePrize(raw: unknown): PrizeConfig | undefined {
  if (!isObj(raw)) return undefined;
  const str = (v: unknown, max: number, fallback: string) => (typeof v === 'string' ? v.slice(0, max) : fallback);
  const notes = typeof raw.notes === 'string' ? raw.notes.slice(0, PRIZE_LIMITS.notes) : '';
  return {
    enabled: raw.enabled === true,
    prize: str(raw.prize, PRIZE_LIMITS.prize, PRIZE_DEFAULTS.prize),
    winners: wholeIn(raw.winners, PRIZE_LIMITS.winners, PRIZE_DEFAULTS.winners),
    drawPrizes: wholeIn(raw.drawPrizes, PRIZE_LIMITS.drawPrizes, PRIZE_DEFAULTS.drawPrizes),
    eligibility: str(raw.eligibility, PRIZE_LIMITS.eligibility, PRIZE_DEFAULTS.eligibility),
    claimHours: wholeIn(raw.claimHours, PRIZE_LIMITS.claimHours, PRIZE_DEFAULTS.claimHours),
    ...(notes ? { notes } : {}),
  };
}

export function loadDraft(): StudioConfig {
  const base: StudioConfig = { title: '', ...STUDIO_DEFAULTS };
  try {
    const raw = localStorage.getItem(DRAFT); if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<StudioConfig> & { v?: unknown };
    if (!isObj(parsed)) return base;
    const { v, ...d } = parsed;
    const merged: StudioConfig = { ...base, ...d, timing: { ...base.timing, ...(isObj(d.timing) ? d.timing : {}) }, formats: { ...base.formats, ...(isObj(d.formats) ? d.formats : {}) } };
    // A start time from an earlier session is never carried over.
    delete merged.scheduledStartAt;
    merged.title = typeof merged.title === 'string' ? merged.title : '';
    merged.questionCount = clampCount(Number(merged.questionCount) || base.questionCount);
    merged.categories = Array.isArray(merged.categories) ? merged.categories.filter((x) => typeof x === 'string') : [];
    if (d.stage === 'preclinical' || d.stage === 'clinical') merged.stage = d.stage; else delete merged.stage;
    merged.structure = d.structure === 'classic' ? 'classic' : 'themed';
    if (isLook(d.look)) merged.look = d.look; else delete merged.look;
    const prize = sanitisePrize(d.prize);
    if (prize) merged.prize = prize; else delete merged.prize;
    merged.finish = sanitiseFinish(d.finish, typeof v === 'number' && v >= DRAFT_VERSION);
    return merged;
  } catch { return base; }
}
/** Keeps the draft on this device, stamped with DRAFT_VERSION (useStudio saves on mount, so a loaded old draft is stamped at once). */
export function saveDraft(c: StudioConfig): void {
  try { localStorage.setItem(DRAFT, JSON.stringify({ v: DRAFT_VERSION, ...c, scheduledStartAt: undefined })); } catch { /* storage unavailable */ }
}
export function clearDraft(): void { try { localStorage.removeItem(DRAFT); } catch { /* storage unavailable */ } }

export function loadSigner(): string {
  try { const v = localStorage.getItem(SIGNER); return typeof v === 'string' ? v.slice(0, MAX_SIGNER) : ''; } catch { return ''; }
}
export function saveSigner(name: string): void {
  const n = name.trim().slice(0, MAX_SIGNER);
  try { if (n) localStorage.setItem(SIGNER, n); } catch { /* storage unavailable */ }
}

// ---------------------------------------------------------------------------
// Words and numbers
// ---------------------------------------------------------------------------
/** "45 s", "14 min 30 s", "15 min", "1 h 5 min". */
export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60); const r = s % 60;
  if (m < 60) return r ? `${m} min ${r} s` : `${m} min`;
  const h = Math.floor(m / 60); const rm = m % 60;
  return rm ? `${h} h ${rm} min` : `${h} h`;
}
/** "14:05", or "1:02:05" past the hour. */
export function formatClock(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const r = s % 60;
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(r).padStart(2, '0')}`;
}
const pad2 = (n: number) => String(n).padStart(2, '0');
/** Epoch ms as the value of a datetime-local input, in the viewer's zone. */
export const toLocalInput = (ms: number) => { const d = new Date(ms); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`; };
/** A datetime-local input's value as epoch ms, or undefined when it is empty or not a time. */
export const fromLocalInput = (v: string): number | undefined => { const t = v ? new Date(v).getTime() : Number.NaN; return Number.isFinite(t) ? t : undefined; };
/** The next quarter hour at least ten minutes away. */
export const nextSlot = (now: number) => { const q = 15 * 60_000; return Math.ceil((now + 10 * 60_000) / q) * q; };

/** "Wednesday 7 October at 19:30" in the viewer's zone (or the zone given). */
export function formatStart(epochMs: number, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hour12: false, timeZone }).formatToParts(new Date(epochMs));
  const get = (t: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')} at ${get('hour')}:${get('minute')}`;
}
/** "19:44" in the viewer's zone. */
export const clockAt = (epochMs: number, timeZone?: string) =>
  new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone }).format(new Date(epochMs));

/** What a prize offers, in one line: "1 × A 12-month MedSnapp subscription for the top player, and 2 more drawn at random …". */
export function prizeLine(p: PrizeConfig): string {
  const top = p.winners > 0 ? `${p.winners} × ${p.prize} for the top ${p.winners === 1 ? 'player' : `${p.winners} players`}` : '';
  const draw = p.drawPrizes > 0
    ? (top ? `, and ${p.drawPrizes} more drawn at random from players who complete the claim form` : `${p.drawPrizes} × ${p.prize}, drawn at random from players who complete the claim form`)
    : '';
  return `${top}${draw}`;
}

export function influencerMessage(o: {
  config: StudioConfig; code: string; questionCount: number; totalMs: number;
  joinUrl: string; hostUrl: string; stageUrl: string; portraitUrl?: string; termsUrl?: string; timeZone?: string;
}): string {
  const { config: c } = o;
  const minutes = Math.max(1, Math.round(o.totalMs / 60_000));
  const name = c.hostName?.trim();
  const when = c.mode === 'manual'
    ? 'It starts when you press Start on your host page. After each question you press Next to move it on.'
    : c.scheduledStartAt !== undefined
      ? `It starts by itself on ${formatStart(c.scheduledStartAt, o.timeZone)} and runs itself from there.`
      : 'It starts when you press Start on your host page, then runs itself.';
  const lines = [
    name ? `Hi ${name},` : 'Hi,',
    '',
    'Your MedSnapp Rounds battle is ready.',
    '',
    c.title.trim(),
    `${o.questionCount} questions, about ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}. ${when}`,
    '',
    'Your host link. Keep it to yourself: it controls the battle.',
    o.hostUrl,
    '',
    'For your audience:',
    `Join at ${o.joinUrl}`,
    `or enter the code ${o.code}`,
    ...(c.prize?.enabled && o.termsUrl ? ['', `The prize: ${prizeLine(c.prize)}. Say "terms apply" whenever you mention it. The terms are here:`, o.termsUrl] : []),
    '',
    c.display === 'screen' ? 'Classroom mode: put the stage view on the big screen. Phones show only the answer marks.' : 'Stage view for OBS or a projector:',
    o.stageUrl,
    ...(o.portraitUrl ? ['', 'Portrait stage (9:16) for Instagram Live Producer or OBS:', o.portraitUrl] : []),
    '',
    'From the host page you can hold after any question, skip a question or end the battle early.',
  ];
  return lines.join('\n');
}
